/**
 * Reusable ImportanceBadge component with tooltip support
 * Displays importance level with context-specific explanations
 * Supports both hover (desktop) and tap (mobile) interactions
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Star, Circle, Minus } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

type ImportanceContext =
  | 'place'           // Geographic locations
  | 'cultural'        // Cultural points and terms
  | 'archaeology'     // Archaeological findings
  | 'historical'      // Historical events
  | 'general';        // Generic importance

interface ImportanceBadgeProps {
  importance: Importance;
  context?: ImportanceContext;
  className?: string;
}

const TOOLTIP_TEXT: Record<ImportanceContext, string> = {
  place: 'How central this location is to the passage',
  cultural: 'How significantly this affects interpretation',
  archaeology: 'How directly this relates to the biblical text',
  historical: 'How directly this impacts biblical events',
  general: 'Relative importance or relevance'
};

const IMPORTANCE_LABELS: Record<Importance, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low'
};

const IMPORTANCE_ICONS = {
  high: Star,
  medium: Circle,
  low: Minus
};

const IMPORTANCE_DESCRIPTIONS: Record<Importance, Record<ImportanceContext, string>> = {
  high: {
    place: 'Primary setting or location central to the passage',
    cultural: 'Significantly changes or deepens interpretation',
    archaeology: 'Directly verifies or illustrates the biblical text',
    historical: 'Directly impacts or provides crucial context for biblical events',
    general: 'Highly relevant or significant'
  },
  medium: {
    place: 'Mentioned in context or provides important background',
    cultural: 'Adds meaningful depth to understanding',
    archaeology: 'Illustrates the historical/cultural context',
    historical: 'Provides helpful background or parallel events',
    general: 'Moderately relevant'
  },
  low: {
    place: 'Passing reference or distant connection',
    cultural: 'Interesting detail but minimal interpretive impact',
    archaeology: 'General background information',
    historical: 'Tangentially related or general historical context',
    general: 'Minor relevance'
  }
};

export function ImportanceBadge({ importance, context = 'general', className = '' }: ImportanceBadgeProps) {
  const [open, setOpen] = useState(false);
  const tooltipText = TOOLTIP_TEXT[context];
  const description = IMPORTANCE_DESCRIPTIONS[importance][context];
  const label = IMPORTANCE_LABELS[importance];
  const Icon = IMPORTANCE_ICONS[importance];

  // Determine badge styling based on importance level
  const variant = 'outline'; // Always use outline variant for consistency in the discovery pattern
  const badgeClassName = importance === 'high'
    ? `text-[10px] bg-primary/5 text-primary border-primary/30 font-medium ${className}`
    : importance === 'medium'
      ? `text-[10px] bg-muted/50 border-border text-muted-foreground font-medium ${className}`
      : `text-[10px] border-border/50 text-muted-foreground/70 font-normal ${className}`;

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <Badge
          variant={variant}
          className={`${badgeClassName} cursor-help flex items-center gap-1 transition-colors`}
          onClick={() => setOpen(!open)}
        >
          <Icon size={11} className="shrink-0" />
          <span className="font-normal opacity-70">Importance:</span> {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <p className="font-semibold">{tooltipText}</p>
          <p className="text-xs opacity-90">{description}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
